import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

/**
 * Map technical keys to French labels
 */
const REFERRAL_REASON_LABELS = {
  'pulpite_irreversible': 'Pulpite irréversible',
  'necrose_pulpaire': 'Nécrose pulpaire',
  'reprise_traitement': 'Reprise de traitement',
  'lesion_periapicale': 'Lésion péri-apicale',
  'felure_fracture': 'Fêlure / fracture suspectée',
  'diagnostic_douleur': 'Diagnostic de douleur',
  'autre': 'Autre'
};

/**
 * Convert technical key to French label
 */
const getReferralReasonLabel = (key) => {
  return REFERRAL_REASON_LABELS?.[key] || key;
};

/**
 * Get logo dimensions based on size preference
 */
const getLogoDimensions = (logoSize) => {
  const sizes = {
    small: { width: 10, height: 10 },
    medium: { width: 15, height: 15 },
    large: { width: 18, height: 18 },
    xlarge: { width: 36, height: 36 }
  };
  return sizes?.[logoSize] || sizes?.medium;
};

/**
 * Get logo X position based on position preference
 */
const getLogoXPosition = (logoPosition, pageWidth, logoWidth, margin) => {
  const positions = {
    left: margin,
    center: (pageWidth - logoWidth) / 2,
    right: pageWidth - margin - logoWidth
  };
  return positions?.[logoPosition] || positions?.left;
};

/**
 * Generate patient initials from firstName and lastName
 */
const getPatientInitials = (patientInfo) => {
  const firstName = patientInfo?.firstName?.trim() || '';
  const lastName = patientInfo?.lastName?.trim() || '';
  
  if (!firstName && !lastName) return 'Non spécifié';
  
  const firstInitial = firstName?.[0]?.toUpperCase() || '';
  const lastInitial = lastName?.[0]?.toUpperCase() || '';
  
  return `${firstInitial}${lastInitial}` || 'Non spécifié';
};

/**
 * Generate compact, professional medical PDF - 1-2 pages maximum
 */
export const generateReferralPDF = async (pdfData, practitionerData = null) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  doc?.setFont('helvetica');
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (2 * margin);
  let yPos = 8; // Reduced from 15 to 8 to position logo higher

  // Colors
  const primaryBlue = [59, 130, 246];
  const darkText = [31, 41, 55];
  const mediumGray = [107, 114, 128];
  const lightGray = [243, 244, 246];
  const borderGray = [229, 231, 235];

  // ============================================
  // PAGE BREAK HELPER FUNCTION
  // ============================================
  const checkPageBreak = (requiredSpace = 10) => {
    if (yPos + requiredSpace > pageHeight - 20) {
      doc?.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // ============================================
  // COMPACT HEADER WITH LOGO
  // ============================================
  
  // Get logo customization preferences
  const logoSize = practitionerData?.logoSize || 'medium';
  const logoPosition = practitionerData?.logoPosition || 'left';
  const logoDimensions = getLogoDimensions(logoSize);
  const logoXPos = getLogoXPosition(logoPosition, pageWidth, logoDimensions?.width, margin);
  
  // Display logo URL if available, otherwise show initials
  if (practitionerData?.logoUrl) {
    try {
      // Add logo image with custom size and position
      doc?.addImage(practitionerData?.logoUrl, 'PNG', logoXPos, yPos, logoDimensions?.width, logoDimensions?.height);
    } catch (error) {
      console.error('Error loading logo, falling back to initials:', error);
      // Fallback to initials if logo fails
      doc?.setFillColor(...primaryBlue);
      doc?.roundedRect(logoXPos, yPos, logoDimensions?.width, logoDimensions?.height, 2, 2, 'F');
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(logoSize === 'xlarge' ? 24 : logoSize === 'large' ? 14 : logoSize === 'medium' ? 10 : 8);
      doc?.setFont('helvetica', 'bold');
      const logoInitials = practitionerData?.logoInitials || 'AT';
      const textXOffset = logoSize === 'xlarge' ? 12 : logoSize === 'large' ? 5 : logoSize === 'medium' ? 3 : 2;
      const textYOffset = logoSize === 'xlarge' ? 23 : logoSize === 'large' ? 12 : logoSize === 'medium' ? 8 : 6;
      doc?.text(logoInitials, logoXPos + textXOffset, yPos + textYOffset);
    }
  } else {
    // Show initials as fallback with custom size and position
    doc?.setFillColor(...primaryBlue);
    doc?.roundedRect(logoXPos, yPos, logoDimensions?.width, logoDimensions?.height, 2, 2, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(logoSize === 'xlarge' ? 24 : logoSize === 'large' ? 14 : logoSize === 'medium' ? 10 : 8);
    doc?.setFont('helvetica', 'bold');
    const logoInitials = practitionerData?.logoInitials || 'AT';
    const textXOffset = logoSize === 'xlarge' ? 12 : logoSize === 'large' ? 5 : logoSize === 'medium' ? 3 : 2;
    const textYOffset = logoSize === 'xlarge' ? 23 : logoSize === 'large' ? 12 : logoSize === 'medium' ? 8 : 6;
    doc?.text(logoInitials, logoXPos + textXOffset, yPos + textYOffset);
  }
  
  // Adjust practitioner name position based on logo position
  const nameXPos = logoPosition === 'center' ? margin : (logoPosition === 'right' ? margin : margin + logoDimensions?.width + 3);
  
  doc?.setTextColor(...darkText);
  doc?.setFontSize(14);
  const practitionerName = practitionerData?.practitionerName 
    ? `${practitionerData?.practitionerTitle} ${practitionerData?.practitionerName}` 
    : 'Dr Arnaud Touati';
  doc?.text(practitionerName, nameXPos, yPos + 6);
  doc?.setFontSize(8);
  doc?.setTextColor(...mediumGray);
  const practiceName = practitionerData?.practiceName || 'Endodontie exclusive';
  doc?.text(practiceName, nameXPos, yPos + 10);
  
  const dateStr = new Date()?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  doc?.setFontSize(7);
  doc?.text(`Généré le ${dateStr}`, pageWidth - margin - 30, yPos + 8);
  
  // Ensure content starts below the logo by accounting for logo height
  yPos += Math.max(logoDimensions?.height + 3, 18); // Use logo height + 3mm spacing, minimum 18mm
  doc?.setDrawColor(...borderGray);
  doc?.setLineWidth(0.3);
  doc?.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  // ============================================
  // COMPACT SECTION HELPER WITH PAGE BREAK SUPPORT
  // ============================================
  const addCompactSection = (title, fields) => {
    // Check if we need a page break before section title
    checkPageBreak(15);
    
    // Section title
    doc?.setFillColor(...lightGray);
    doc?.rect(margin, yPos, contentWidth, 6, 'F');
    doc?.setTextColor(...darkText);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text(title, margin + 2, yPos + 4);
    yPos += 8;

    // Fields in compact 2-column layout
    fields?.forEach((field, idx) => {
      if (field?.fullWidth) {
        // Check page break before label
        checkPageBreak(8);
        
        doc?.setFontSize(7);
        doc?.setTextColor(...mediumGray);
        doc?.setFont('helvetica', 'bold');
        doc?.text(field?.label, margin + 2, yPos);
        yPos += 3;
        
        doc?.setFontSize(8);
        doc?.setTextColor(...darkText);
        doc?.setFont('helvetica', 'normal');
        const lines = doc?.splitTextToSize(field?.value || 'Non spécifié', contentWidth - 4);
        
        // Render each line with page break check
        lines?.forEach(line => {
          checkPageBreak(5);
          doc?.text(line, margin + 2, yPos);
          yPos += 3.5;
        });
        yPos += 1;
      } else {
        const isLeft = idx % 2 === 0;
        const xPos = isLeft ? margin + 2 : margin + (contentWidth / 2) + 2;
        const fieldWidth = (contentWidth / 2) - 4;
        
        // Check page break for two-column fields
        if (isLeft) {
          checkPageBreak(10);
        }
        
        doc?.setFontSize(7);
        doc?.setTextColor(...mediumGray);
        doc?.setFont('helvetica', 'bold');
        doc?.text(field?.label, xPos, yPos);
        
        doc?.setFontSize(8);
        doc?.setTextColor(...darkText);
        doc?.setFont('helvetica', 'normal');
        const valueLines = doc?.splitTextToSize(field?.value || 'Non spécifié', fieldWidth);
        doc?.text(valueLines?.[0] || 'Non spécifié', xPos, yPos + 3.5);
        
        if (!isLeft || idx === fields?.length - 1) {
          yPos += 7;
        }
      }
    });
    
    yPos += 2;
  };

  // ============================================
  // REFERRING DENTIST
  // ============================================
  addCompactSection('Confrère adresseur', [
    { label: 'Nom / Prénom', value: pdfData?.referringDentist?.fullName },
    { label: 'Cabinet', value: pdfData?.referringDentist?.practiceName },
    { label: 'Email', value: pdfData?.referringDentist?.email },
    { label: 'Téléphone', value: pdfData?.referringDentist?.phone }
  ]);

  // ============================================
  // PATIENT INFO
  // ============================================
  const patientFullName = `${pdfData?.patientInfo?.firstName?.trim() || ''} ${pdfData?.patientInfo?.lastName?.trim() || ''}`?.trim() || 'Non spécifié';
  const sexLabel = pdfData?.patientInfo?.sex === 'male' ? 'Homme' : 
                   pdfData?.patientInfo?.sex === 'female' ? 'Femme' : 
                   pdfData?.patientInfo?.sex === 'other' ? 'Autre' : 'Non spécifié';
  
  const patientFields = [
    { label: 'Nom et Prénom', value: patientFullName, fullWidth: true },
    { label: 'Année de naissance', value: String(pdfData?.patientInfo?.yearOfBirth || 'Non spécifié') },
    { label: 'Sexe', value: sexLabel }
  ];

  // Add phone if available
  if (pdfData?.patientInfo?.phone?.trim()) {
    patientFields?.push({ label: 'Téléphone', value: pdfData?.patientInfo?.phone?.trim() });
  }

  // Add email if available
  if (pdfData?.patientInfo?.email?.trim()) {
    patientFields?.push({ label: 'Email', value: pdfData?.patientInfo?.email?.trim() });
  }

  addCompactSection('Patient', patientFields);

  // ============================================
  // CLINICAL INFORMATION
  // ============================================
  const teethText = (pdfData?.toothNumbers && pdfData?.toothNumbers?.length > 0) 
    ? pdfData?.toothNumbers?.join(', ') 
    : 'Aucune dent spécifiée';
  
  const reasonsText = (pdfData?.referralReasons && pdfData?.referralReasons?.length > 0)
    ? pdfData?.referralReasons?.map(r => getReferralReasonLabel(r))?.join(', ')
    : 'Aucun motif spécifié';

  addCompactSection('Informations cliniques', [
    { label: 'Dent(s) concernée(s) (FDI)', value: teethText, fullWidth: true },
    { label: 'Motif(s) d\'adressage', value: reasonsText, fullWidth: true }
  ]);

  // ============================================
  // CLINICAL NOTES
  // ============================================
  if (pdfData?.clinicalNotes?.trim()) {
    addCompactSection('Notes cliniques', [
      { label: 'Observations', value: pdfData?.clinicalNotes?.trim(), fullWidth: true }
    ]);
  }

  // ============================================
  // IMAGING
  // ============================================
  if (pdfData?.imaging && (pdfData?.imaging?.downloadLink || pdfData?.imaging?.password || pdfData?.imaging?.imagingCenter)) {
    const imagingFields = [];
    if (pdfData?.imaging?.downloadLink) {
      imagingFields?.push({ label: 'Lien de téléchargement', value: pdfData?.imaging?.downloadLink, fullWidth: true });
    }
    if (pdfData?.imaging?.password) {
      imagingFields?.push({ label: 'Mot de passe', value: pdfData?.imaging?.password });
    }
    if (pdfData?.imaging?.imagingCenter) {
      imagingFields?.push({ label: 'Centre d\'imagerie', value: pdfData?.imaging?.imagingCenter });
    }
    addCompactSection('Imagerie / CBCT', imagingFields);
  }

  // ============================================
  // UPLOADED FILES LIST
  // ============================================
  if (pdfData?.uploadedFiles && pdfData?.uploadedFiles?.length > 0) {
    checkPageBreak(15);
    
    doc?.setFillColor(...lightGray);
    doc?.rect(margin, yPos, contentWidth, 6, 'F');
    doc?.setTextColor(...darkText);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Pièces jointes', margin + 2, yPos + 4);
    yPos += 8;

    pdfData?.uploadedFiles?.forEach(file => {
      checkPageBreak(5);
      doc?.setFontSize(7);
      doc?.setTextColor(...darkText);
      doc?.setFont('helvetica', 'normal');
      const fileSize = (file?.size / 1024)?.toFixed(1);
      doc?.text(`• ${file?.name} (${fileSize} Ko)`, margin + 2, yPos);
      yPos += 3.5;
    });
    yPos += 3;
  }

  // ============================================
  // SIGNATURE SECTION (if provided)
  // ============================================
  if (practitionerData?.signatureUrl) {
    // Check if we need space for signature
    checkPageBreak(30);
    
    yPos += 5;
    doc?.setDrawColor(...borderGray);
    doc?.setLineWidth(0.2);
    doc?.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
    
    doc?.setFontSize(8);
    doc?.setTextColor(...mediumGray);
    doc?.setFont('helvetica', 'italic');
    doc?.text('Signature du praticien', margin, yPos);
    yPos += 5;
    
    try {
      // Add signature image
      doc?.addImage(practitionerData?.signatureUrl, 'PNG', margin, yPos, 40, 15);
      yPos += 18;
    } catch (error) {
      console.error('Error loading signature:', error);
      yPos += 5;
    }
  }

  // ============================================
  // IMAGES ON PAGE 2 (if any)
  // ============================================
  const imageFiles = pdfData?.uploadedFiles?.filter(file => file?.type?.startsWith('image/')) || [];
  const attachedImages = pdfData?.attachedImages || [];
  const allImages = [...imageFiles, ...attachedImages];
  
  if (allImages?.length > 0) {
    doc?.addPage();
    yPos = margin;
    
    doc?.setFillColor(...lightGray);
    doc?.rect(margin, yPos, contentWidth, 6, 'F');
    doc?.setTextColor(...darkText);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Images et Radiographies', margin + 2, yPos + 4);
    yPos += 10;

    for (const img of allImages) {
      try {
        // Get image data (base64 or URL)
        let imageData = img?.base64 || img?.preview || img?.url;
        
        // If URL, fetch and convert to base64
        if (imageData && imageData?.startsWith('http')) {
          try {
            const response = await fetch(imageData);
            const blob = await response?.blob();
            imageData = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader?.result);
              reader.readAsDataURL(blob);
            });
          } catch (fetchError) {
            console.error('Error fetching image:', fetchError);
            continue;
          }
        }
        
        if (imageData) {
          // Check if we need a new page
          if (yPos > pageHeight - 90) {
            doc?.addPage();
            yPos = margin;
          }

          // Image caption
          doc?.setFontSize(7);
          doc?.setTextColor(...mediumGray);
          doc?.setFont('helvetica', 'italic');
          const imageName = img?.name || img?.fileName || 'Image médicale';
          doc?.text(imageName, margin, yPos);
          yPos += 4;

          // Add image with border (medical imaging layout)
          const imgWidth = contentWidth;
          const imgHeight = 80; // Larger for medical images
          
          // Border
          doc?.setDrawColor(...borderGray);
          doc?.setLineWidth(0.3);
          doc?.rect(margin, yPos, imgWidth, imgHeight);
          
          try {
            // Detect image format
            const format = imageData?.includes('data:image/png') ? 'PNG' : 'JPEG';
            doc?.addImage(imageData, format, margin + 1, yPos + 1, imgWidth - 2, imgHeight - 2);
          } catch (err) {
            console.error('Error adding image to PDF:', err);
            // Add placeholder text if image fails
            doc?.setFontSize(8);
            doc?.setTextColor(...mediumGray);
            doc?.text('Image non disponible', margin + imgWidth / 2, yPos + imgHeight / 2, { align: 'center' });
          }
          
          yPos += imgHeight + 8;
        }
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }
  }

  // ============================================
  // COMPACT FOOTER ON ALL PAGES
  // ============================================
  const totalPages = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc?.setPage(i);
    const footerY = pageHeight - 12;
    
    doc?.setDrawColor(...borderGray);
    doc?.setLineWidth(0.2);
    doc?.line(margin, footerY - 2, pageWidth - margin, footerY - 2);
    
    doc?.setFontSize(7);
    doc?.setTextColor(...darkText);
    doc?.setFont('helvetica', 'bold');
    const footerTitle = practitionerData?.practitionerName 
      ? `Cabinet d'endodontie – ${practitionerData?.practitionerTitle} ${practitionerData?.practitionerName}` 
      : 'Cabinet d\'endodontie – Dr Arnaud Touati';
    doc?.text(footerTitle, margin, footerY + 2);
    
    doc?.setFontSize(6);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...mediumGray);
    const footerAddress = practitionerData?.address && practitionerData?.phone && practitionerData?.email
      ? `${practitionerData?.address} • ${practitionerData?.phone} • ${practitionerData?.email}`
      : '15 rue Lavoisier, 25000 Besançon • 03 63 42 92 58 • cabinetdentaire.lavoisier@gmail.com';
    doc?.text(footerAddress, margin, footerY + 5);
    
    doc?.setFontSize(5);
    doc?.setTextColor(...lightGray);
    doc?.text('Conformité RGPD : Données traitées côté client uniquement.', margin, footerY + 8);
  }

  return doc;
};

/**
 * Generate and download PDF
 */
export const downloadReferralPDF = async (pdfData, patientName, referenceNumber, practitionerData = null) => {
  const doc = await generateReferralPDF(pdfData, practitionerData);
  const fileName = `Adressage_Endodontie_${patientName}_${referenceNumber}.pdf`;
  doc?.save(fileName);
};

/**
 * Generate PDF as Blob (for sending to backend)
 * Returns a Blob object that can be converted to base64
 */
export const generateReferralPDFBlob = async (pdfData, practitionerData = null) => {
  const doc = await generateReferralPDF(pdfData, practitionerData);
  // Wait for any async operations to complete before generating blob
  await new Promise(resolve => setTimeout(resolve, 100));
  // Generate blob from PDF document
  const blob = doc?.output('blob');
  return blob;
};

/**
 * Convert Blob to base64 string (without data URI prefix)
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} Base64 string without prefix
 */
export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove the data URI prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};